import { getCellKey } from "./boardCells";
import {
    resolveDragging,
    type BoardDraggingState,
    type BoardDragModel,
} from "./boardDragState";
import {
    getKeyboardDropTarget,
    type KeyboardDropTargetArgs,
} from "./boardDropTargets";
import { locateBoardEntry, type BoardDropTarget } from "./boardMove";
import { getHeldPlaceRefusal, isPlaceHeldInSort } from "./boardPlaceSpot";

/** Where the copy shows in its target cell now; the target index elsewhere. */
const getShownIndex = <TItem>(
    model: BoardDragModel<TItem>,
    current: BoardDraggingState,
) => {
    const location = locateBoardEntry(
        resolveDragging(model, current).cells,
        current.activeKey,
    );
    return location !== null &&
        getCellKey(location.entry.placement) ===
            getCellKey(current.target.placement)
        ? location.index
        : current.target.index;
};

/**
 * The drag once the place modifier is pressed or released; `current` itself
 * when that changes nothing. Placing starts where the card shows, so a
 * keyboard step moves it one slot from there; a pointer drag re-resolves
 * its hover right after.
 */
export const setPlaceHeld = <TItem>({
    model,
    current,
    isPlaceHeld,
}: {
    model: BoardDragModel<TItem>;
    current: BoardDraggingState;
    isPlaceHeld: boolean;
}): BoardDraggingState => {
    const next = { ...current, isPlaceHeld, isSlotInstant: true };
    const isHeld = isPlaceHeldInSort(model, next);
    if (isHeld === isPlaceHeldInSort(model, current)) {
        return current;
    }
    // Back in sort order the cell has no slots again.
    const index = isHeld ? getShownIndex(model, current) : 0;
    return { ...next, target: { ...current.target, index } };
};

/**
 * One arrow key step. Held on a sorted board, up and down skip the spots
 * `canPlaceAt` refuses in the cell; with none left that way the step goes
 * on as an unheld one would (the next lane, or nowhere). `skippedRefusal`
 * is the reason for the last spot skipped.
 */
export const getKeyboardPlaceStep = <TItem>(
    model: BoardDragModel<TItem>,
    current: BoardDraggingState,
    args: Omit<KeyboardDropTargetArgs<TItem>, "target">,
) => {
    const cellKey = getCellKey(current.target.placement);
    const isVertical = args.direction === "up" || args.direction === "down";
    const isInCell = (
        target: BoardDropTarget | null,
    ): target is BoardDropTarget =>
        isVertical &&
        target !== null &&
        getCellKey(target.placement) === cellKey;
    let target = getKeyboardDropTarget({ ...args, target: current.target });
    let skippedRefusal: string | undefined;
    while (isInCell(target)) {
        const refusal = getHeldPlaceRefusal(model, { ...current, target });
        if (refusal === undefined) {
            break;
        }
        skippedRefusal = refusal;
        target = getKeyboardDropTarget({ ...args, target });
    }
    return { target, skippedRefusal };
};
